import {
  MapContainer,
  TileLayer,
  Marker,
  Popup,
  useMap,
  ZoomControl,
} from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import offices from "./data/offices.json";
import {
  Box,
  Select,
  FormControl,
  FormLabel,
  Heading,
  Text,
  VStack,
  HStack,
  useTheme,
  Grid,
  GridItem,
  Button,
  Flex,
  Link,
} from "@chakra-ui/react";

// ฟังก์ชันสร้างไอคอน
const createColoredIcon = (regionOffice) => {
  const colorMap = {
    "สำนักงานปศุสัตว์เขต 1": "gold",
    "สำนักงานปศุสัตว์เขต 2": "green",
    "สำนักงานปศุสัตว์เขต 3": "red",
    "สำนักงานปศุสัตว์เขต 4": "orange",
    "สำนักงานปศุสัตว์เขต 5": "yellow",
    "สำนักงานปศุสัตว์เขต 6": "violet",
    "สำนักงานปศุสัตว์เขต 7": "grey",
    "สำนักงานปศุสัตว์เขต 8": "black",
    "สำนักงานปศุสัตว์เขต 9": "blue",
  };

  const color = colorMap[regionOffice] || "blue";

  return new L.Icon({
    iconUrl: `/markers/marker-icon-2x-${color}.png`,
    shadowUrl: "/markers/marker-shadow.png",
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    popupAnchor: [1, -34],
    shadowSize: [41, 41],
  });
};

// Component เพื่อควบคุมการซูมและตำแหน่งของแผนที่
function MapController({ filteredOffices, selectedRegion, selectedProvince }) {
  const map = useMap();

  useEffect(() => {
    if (filteredOffices.length === 0) return;

    if (selectedProvince) {
      // ซูมไปที่จังหวัดที่เลือก
      const office = filteredOffices.find((office) => {
        let officeProvince = "";
        if (office.name.includes("จังหวัด")) {
          officeProvince = office.name.split("จังหวัด")[1].trim();
        } else if (office.address.includes("จังหวัด")) {
          officeProvince = office.address.split("จังหวัด")[1]?.trim();
        } else {
          officeProvince = office.name
            .replace("สำนักงานปศุสัตว์จังหวัด", "")
            .trim();
        }
        return officeProvince === selectedProvince;
      });

      if (office) {
        map.setView([office.latitude, office.longitude], 12); // ซูมระดับจังหวัด
      }
    } else if (selectedRegion) {
      // ซูมไปที่เขตที่เลือก
      const regionOffices = filteredOffices.filter(
        (office) => office.regionalOffice === selectedRegion
      );
      if (regionOffices.length > 0) {
        const bounds = L.latLngBounds(
          regionOffices.map((office) => [office.latitude, office.longitude])
        );
        map.fitBounds(bounds, { padding: [50, 50], maxZoom: 9 });
      }
    } else {
      // กลับไปที่มุมมองเริ่มต้น
      map.setView([13.7563, 100.5018], 6);
    }
  }, [filteredOffices, selectedRegion, selectedProvince, map]);

  return null;
}

export default function MapOffice() {
  const theme = useTheme();
  const navigate = useNavigate();
  const [selectedRegion, setSelectedRegion] = useState("");
  const [selectedProvince, setSelectedProvince] = useState("");

  // สร้างรายการจังหวัดที่ไม่ซ้ำ โดยกรองตามเขตที่เลือก
  const uniqueProvinces = [
    ...new Set(
      offices
        .filter(
          (office) =>
            !selectedRegion || office.regionalOffice === selectedRegion
        )
        .map((office) => {
          if (office.name.includes("จังหวัด")) {
            return office.name.split("จังหวัด")[1].trim();
          }
          if (office.address.includes("จังหวัด")) {
            return office.address.split("จังหวัด")[1]?.trim();
          }
          return office.name.replace("สำนักงานปศุสัตว์จังหวัด", "").trim();
        })
        .filter(Boolean)
    ),
  ].sort();

  // กรองข้อมูล
  const filteredOffices = offices.filter((office) => {
    const matchRegion = selectedRegion
      ? office.regionalOffice === selectedRegion
      : true;

    let officeProvince = "";
    if (office.name.includes("จังหวัด")) {
      officeProvince = office.name.split("จังหวัด")[1].trim();
    } else if (office.address.includes("จังหวัด")) {
      officeProvince = office.address.split("จังหวัด")[1]?.trim();
    } else {
      officeProvince = office.name
        .replace("สำนักงานปศุสัตว์จังหวัด", "")
        .trim();
    }

    const matchProvince = selectedProvince
      ? officeProvince === selectedProvince
      : true;
    return matchRegion && matchProvince;
  });

  return (
    <Box h="100%" w="100%" position="relative">
      {/* Filter Panel */}
      <Box
        position="absolute"
        top={0}
        left={0}
        right={0}
        zIndex={1000}
        bg="white"
        p={4}
        boxShadow="md"
      >
        <Flex
          direction={{ base: "column", md: "row" }}
          align="center"
          justify="space-between"
          gap={4}
          maxW="100%"
          mx="auto"
        >
          <FormControl w={{ base: "100%", md: "200px" }}>
            <FormLabel fontSize="sm">เขตสำนักงาน</FormLabel>
            <Select
              value={selectedRegion}
              onChange={(e) => {
                setSelectedRegion(e.target.value);
                setSelectedProvince(""); // รีเซ็ตจังหวัดเมื่อเปลี่ยนเขต
              }}
              placeholder="ทุกเขต"
              size="sm"
            >
              {[...new Set(offices.map((office) => office.regionalOffice))].map(
                (region, idx) => (
                  <option key={idx} value={region}>
                    {region}
                  </option>
                )
              )}
            </Select>
          </FormControl>

          <FormControl w={{ base: "100%", md: "200px" }}>
            <FormLabel fontSize="sm">จังหวัด</FormLabel>
            <Select
              value={selectedProvince}
              onChange={(e) => setSelectedProvince(e.target.value)}
              placeholder="ทุกจังหวัด"
              size="sm"
            >
              {uniqueProvinces.map((province, idx) => (
                <option key={idx} value={province}>
                  {province}
                </option>
              ))}
            </Select>
          </FormControl>

          <Button
            colorScheme="iPurple"
            onClick={() => {
              setSelectedRegion("");
              setSelectedProvince("");
            }}
            w={{ base: "100%", md: "auto" }}
            size="sm"
          >
            ล้างตัวกรอง
          </Button>
        </Flex>
      </Box>

      {/* Map */}
      <MapContainer
        center={[13.7563, 100.5018]}
        zoom={7}
        style={{ height: "100%", width: "100%" }}
        zoomControl={false}
      >
        <TileLayer
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          attribution="© OpenStreetMap contributors"
        />
        <ZoomControl position="bottomleft" />
        {filteredOffices.map((office, idx) => (
          <Marker
            key={idx}
            position={[office.latitude, office.longitude]}
            icon={createColoredIcon(office.regionalOffice)}
          >
            <Popup h={"200px"} maxWidth="300">
              <VStack align="start">
                <Heading size="sm">{office.name}</Heading>
                <Text> ที่อยู่: {office.address}</Text>
                <Text>
                  <Link href={`tel:${office.phone.replace(/\s+/g, "")}`}>
                    โทร: {office.phone}
                  </Link><br /><br />
                  <Link href={`mailto:${office.email}`}>
                    อีเมล์: {office.email}
                  </Link><br /><br />
                  <Link
                    href={office.website}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    เว็บไซต์: {office.website}
                  </Link>
                </Text>
                <HStack spacing={2} mt={2}>
                  <Button
                    size="sm"
                    w="100px"
                    colorScheme="iTeal"
                    onClick={() => {
                      const googleMapsUrl = `https://www.google.com/maps/dir/?api=1&destination=${office.latitude},${office.longitude}`;
                      window.open(googleMapsUrl, "_blank");
                    }}
                  >
                    นำทาง
                  </Button>
                  <Button
                    size="sm"
                    w="100px"
                    colorScheme="iBlue"
                    onClick={() => {
                      navigate(
                        `/mapofficelocation?office=${encodeURIComponent(
                          office.name
                        )}`
                      );
                    }}
                  >
                    วัคซีนคงคลัง
                  </Button>
                </HStack>
              </VStack>
            </Popup>
          </Marker>
        ))}

        <MapController
          filteredOffices={filteredOffices}
          selectedRegion={selectedRegion}
          selectedProvince={selectedProvince}
        />
      </MapContainer>
    </Box>
  );
}
